import { changePassword } from "@actions/profile";
import { useStrongPasswordValidator } from "@utils/hooks.js";
import PropTypes from "prop-types";
import React, { memo, useCallback, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import { BTTO_PROFILE_COLORS } from "../../shared/elements";
import { BettoPrimaryButton } from "../BettoButton";

const FormRoot = styled.form`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const FieldGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const FieldLabel = styled.label`
	color: ${BTTO_PROFILE_COLORS.textMuted};
	font-size: 12px;
	font-weight: 500;
`;

const FieldInput = styled.input`
	height: 44px;
	padding: 0 14px;
	border-radius: 8px;
	border: 1px solid ${BTTO_PROFILE_COLORS.border};
	background: ${BTTO_PROFILE_COLORS.surfaceRaised};
	color: ${BTTO_PROFILE_COLORS.text};
	font-size: 14px;
`;

const ErrorText = styled.span`
	color: ${BTTO_PROFILE_COLORS.danger};
	font-size: 12px;
`;

export const BettoChangePassword = memo(({ translate, changePassword, user }) => {
	const [values, setValues] = useState({
		PreviousPassword: "",
		ProposedPassword: "",
		confirm_password: "",
	});
	const [error, setError] = useState("");

	const strongPasswordValidator = useStrongPasswordValidator(translate);

	const handleChange = useCallback(
		(field) => (event) => {
			setValues((prev) => ({ ...prev, [field]: event.target.value }));
			setError("");
		},
		[]
	);

	const handleSubmit = useCallback(
		async (event) => {
			event.preventDefault();
			if (!values.PreviousPassword || !values.ProposedPassword || !values.confirm_password) {
				return;
			}
			if (values.ProposedPassword !== values.confirm_password) {
				setError(translate("NOT_MATCH_PASS"));
				return;
			}

			if (strongPasswordValidator) {
				try {
					await strongPasswordValidator(null, values.ProposedPassword);
				} catch (validationError) {
					setError(validationError?.message || translate("GENERIC_ERROR"));
					return;
				}
			}

			changePassword(user?.username, values, () => {
				setValues({ PreviousPassword: "", ProposedPassword: "", confirm_password: "" });
				setError("");
			});
		},
		[changePassword, strongPasswordValidator, translate, user?.username, values]
	);

	const isDisabled =
		!values.PreviousPassword || !values.ProposedPassword || !values.confirm_password;

	return (
		<FormRoot onSubmit={handleSubmit}>
			<FieldGroup>
				<FieldLabel>{translate("Current Password")}</FieldLabel>
				<FieldInput
					type="password"
					value={values.PreviousPassword}
					onChange={handleChange("PreviousPassword")}
				/>
			</FieldGroup>
			<FieldGroup>
				<FieldLabel>{translate("New Password")}</FieldLabel>
				<FieldInput
					type="password"
					value={values.ProposedPassword}
					onChange={handleChange("ProposedPassword")}
				/>
			</FieldGroup>
			<FieldGroup>
				<FieldLabel>{translate("Confirm New Password")}</FieldLabel>
				<FieldInput
					type="password"
					value={values.confirm_password}
					onChange={handleChange("confirm_password")}
				/>
			</FieldGroup>
			{error ? <ErrorText>{error}</ErrorText> : null}
			<BettoPrimaryButton
				type="submit"
				disabled={isDisabled}
				fullWidth>
				{translate("CHANGE_PASSWORD")}
			</BettoPrimaryButton>
		</FormRoot>
	);
});

BettoChangePassword.displayName = "BettoChangePassword";

BettoChangePassword.propTypes = {
	translate: PropTypes.func.isRequired,
	changePassword: PropTypes.func.isRequired,
	user: PropTypes.object,
};

const mapDispatchToProps = { changePassword };

export default connect(null, mapDispatchToProps)(BettoChangePassword);
